import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, CalendarCheck, ShieldCheck, Truck, Banknote, ChevronDown } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1567808291548-fc3ee04dbcf0?crop=entropy&cs=srgb&fm=jpg&q=85";

const lines = [
  { text: "REMPLACEMENT", gold: false },
  { text: "PARE-BRISE", gold: true },
  { text: "À DOMICILE OU SUR PLACE", gold: false },
];

const pills = [
  { icon: Banknote, label: "0€ avance de frais" },
  { icon: Truck, label: "Déplacement gratuit" },
  { icon: ShieldCheck, label: "Agréé toutes assurances" },
];

export const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 220]);
  const opacity = useTransform(scrollY, [0, 600], [1, 0.3]);

  return (
    <section data-testid="hero-section" className="relative flex min-h-screen items-center overflow-hidden bg-[#050505]">
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Voiture de nuit à Bordeaux"
          className="h-[120%] w-full object-cover object-center opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/80 to-[#050505]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/60" />
      </motion.div>
      <div className="pointer-events-none absolute -left-40 top-1/3 h-[480px] w-[480px] rounded-full bg-[#D4AF37]/10 blur-[140px]" />

      <motion.div style={{ opacity }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 pt-36 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          data-testid="hero-badge"
          className="mb-8 inline-flex items-center gap-3 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-5 py-2"
        >
          <span className="h-2 w-2 animate-pulse rounded-full bg-[#D4AF37]" />
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#E6CA65]">
            N°1 du pare-brise à Bordeaux
          </span>
        </motion.div>

        <h1 className="font-display uppercase leading-[0.95] tracking-tight">
          {lines.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.25 + i * 0.16, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className={`block text-5xl sm:text-7xl lg:text-8xl ${line.gold ? "text-gold-gradient" : "text-white"}`}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-6 max-w-xl text-sm leading-relaxed text-zinc-300 sm:text-base"
        >
          Vitres de qualité, équipements d'origine. Intervention rapide &amp; propre, à votre domicile ou dans nos
          locaux de Villenave d'Ornon — Bordeaux &amp; ses alentours.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.0, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          data-testid="hero-offer"
          className="mt-10 inline-flex items-stretch overflow-hidden rounded-2xl border border-[#D4AF37]/40 bg-[#0D0D0F]/80 backdrop-blur"
        >
          <div className="flex items-center bg-gradient-to-br from-[#F3E5AB] via-[#D4AF37] to-[#B8860B] px-6 py-4">
            <span className="font-display text-5xl leading-none text-black sm:text-6xl">200€</span>
          </div>
          <div className="flex flex-col justify-center px-6 py-4">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E6CA65]">
              Jusqu'à 200€ offerts*
            </span>
            <span className="mt-1 text-sm text-zinc-300">+ plein de cadeaux à chaque intervention</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.7 }}
          className="mt-8 flex flex-wrap gap-3"
        >
          {pills.map((p) => (
            <span
              key={p.label}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-medium text-zinc-200"
            >
              <p.icon className="h-3.5 w-3.5 text-[#D4AF37]" />
              {p.label}
            </span>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.7 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#rendez-vous"
            data-testid="hero-booking-cta"
            className="group inline-flex items-center gap-3 rounded-full bg-[#D4AF37] px-8 py-4 text-sm font-bold uppercase tracking-widest text-black transition-colors hover:bg-[#F3E5AB]"
          >
            <CalendarCheck className="h-5 w-5 transition-transform duration-300 group-hover:-rotate-6" />
            Réserver mon intervention
          </a>
          <a
            href="tel:0602259560"
            data-testid="hero-phone-cta"
            className="inline-flex items-center gap-3 rounded-full border border-white/20 px-8 py-4 text-sm font-bold uppercase tracking-widest text-white transition-colors hover:border-[#D4AF37] hover:text-[#E6CA65]"
          >
            <Phone className="h-5 w-5" />
            06 02 25 95 60
          </a>
        </motion.div>
      </motion.div>

      <motion.a
        href="#avantages"
        data-testid="hero-scroll-cue"
        aria-label="Défiler vers le bas"
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-zinc-500 transition-colors hover:text-[#D4AF37]"
      >
        <ChevronDown className="h-7 w-7" />
      </motion.a>
    </section>
  );
};
